import React, { FormEvent, useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import DstTabsVertical from "../../../../components/DstTabsVertical/DstTabsVertical";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { MultiSelectLiveDropdownPropsSelectItems } from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown.d";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { CohortIntensity } from "../../../../models/CohortIntensity";
import { CursusStageCadre } from "../../../../models/CursusStageCadre";
import { LanguageEnum } from "../../../../models/Language";
import translate from "../../../../services/Translate";
import { Flag } from "../../Course.d";
import { fetchFlags } from "../../Course.function";
import {
  ComplianceForm,
  CreateCursusStagePopupProps,
  CursusStageForm,
  SkillBlockForm,
} from "./CreateCursusStagePopup.d";
import {
  COMPLIANCE_TYPES,
  buildCreateCursusStagePayload,
  createCursusStage,
  emptyComplianceForm,
  emptyCursusStageForm,
  isComplianceValid,
} from "./CreateCursusStagePopup.function";
import "./CreateCursusStagePopup.scss";

const CreateCursusStagePopup = ({
  cursusId,
  onStageCreated,
  language,
  openPopup,
  setOpenPopup,
}: CreateCursusStagePopupProps) => {
  const [activeTab, setActiveTab] = useState("general");

  const [form, setForm] = useState<CursusStageForm>(emptyCursusStageForm);
  const [compliance, setCompliance] = useState<ComplianceForm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLeaders, setSelectedLeaders] = useState<MultiSelectLiveDropdownPropsSelectItems[]>([]);

  const [selectedFlags, setSelectedFlags] = useState<Flag[]>([]);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [isFlagsLoading, setIsFlagsLoading] = useState(false);

  useEffect(() => {
    if (openPopup) {
      fetchFlags(language, setFlags, setIsFlagsLoading);
    }
  }, [openPopup, language]);

  const _updateField = (key: keyof CursusStageForm, value: string | number | boolean | null) => {
    setForm((previousForm) => ({
      ...previousForm,
      [key]: value,
    }));
  };

  const _handleClose = () => {
    if (!isSaving) {
      setOpenPopup(false);
    }
  };

  const areDatesValid = form.dt_start !== "" && form.dt_end !== "" && form.dt_end > form.dt_start;

  const _handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();

    if (isSaving || !_isFormValid) {
      return;
    }

    createCursusStage(
      cursusId,
      buildCreateCursusStagePayload(
        form,
        compliance,
        selectedFlags.map((flag) => flag.id)
      ),
      language,
      setIsSaving,
      (stageId: number) => {
        setOpenPopup(false);
        onStageCreated(stageId);
      }
    );
  };

  const _isFormValid =
    form.deployment_name.trim() !== "" &&
    form.cursus_prefix.trim() !== "" &&
    form.deployment_name.length <= 255 &&
    form.cursus_prefix.length <= 255 &&
    [form.total_hours, form.hours_spent_on_project, form.hours_spent_on_modules, form.hours_spent_on_masterclass].every(
      (value) => value === null || (Number.isInteger(value) && value >= 0)
    ) &&
    (form.priority === null || Number.isInteger(form.priority)) &&
    areDatesValid &&
    form.language !== "" &&
    isComplianceValid(compliance);

  const textArea = (
    key:
      | "objectives"
      | "prerequisites"
      | "content"
      | "training_organization"
      | "evaluations"
      | "end_of_formation_document"
      | "mediator"
      | "pedagogy_leader_references"
      | "pedagogy_leader_diplomas",
    rows = 3
  ) => (
    <Col md={6}>
      <Form.Label htmlFor={`create-stage-${key}`}>
        {translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.${key.toUpperCase()}`)}
      </Form.Label>

      <Form.Control
        id={`create-stage-${key}`}
        as="textarea"
        rows={rows}
        value={form[key]}
        onChange={(event) => _updateField(key, event.target.value)}
      />
    </Col>
  );

  const _updateComplianceField = (key: string, value: string | number | boolean | null | SkillBlockForm[]) => {
    if (!compliance) {
      return;
    }

    setCompliance({
      ...compliance,
      [key]: value,
    });
  };

  const _updateSkillBlock = (index: number, key: string, value: string | boolean) => {
    if (!compliance) {
      return;
    }

    _updateComplianceField(
      "skill_blocks",
      compliance.skill_blocks.map((block, position) =>
        position === index
          ? {
              ...block,
              [key]: value,
            }
          : block
      )
    );
  };

  const _handleSkillBlockDragEnd = (result: DropResult) => {
    if (!compliance || !result.destination) {
      return;
    }

    const blocks = [...compliance.skill_blocks];

    const [movedBlock] = blocks.splice(result.source.index, 1);

    blocks.splice(result.destination.index, 0, movedBlock);

    _updateComplianceField(
      "skill_blocks",
      blocks.map((block, order) => ({
        ...block,
        order,
      }))
    );
  };

  const overallInformationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      <h3 className="mb-4">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.OVERALL_INFORMATION")}</h3>

      <Row className="g-3">
        <Col md={6}>
          <Form.Label htmlFor="stage-deployment-name">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.DEPLOYMENT_NAME")} />
          </Form.Label>

          <Form.Control
            id="stage-deployment-name"
            maxLength={255}
            value={form.deployment_name}
            onChange={(event) => _updateField("deployment_name", event.target.value)}
            required
          />
        </Col>

        <Col md={6}>
          <Form.Label htmlFor="stage-cursus-prefix">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.CURSUS_PREFIX")} />
          </Form.Label>

          <Form.Control
            id="stage-cursus-prefix"
            maxLength={255}
            value={form.cursus_prefix}
            onChange={(event) => _updateField("cursus_prefix", event.target.value)}
            required
          />
        </Col>

        <Col md={6}>
          <Form.Label htmlFor="stage-start">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.DT_START")} />
          </Form.Label>

          <Form.Control
            type="date"
            id="stage-start"
            value={form.dt_start}
            onChange={(event) => _updateField("dt_start", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>

        <Col md={6}>
          <Form.Label htmlFor="stage-end">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.DT_END")} />
          </Form.Label>

          <Form.Control
            type="date"
            id="stage-end"
            value={form.dt_end}
            min={form.dt_start}
            onChange={(event) => _updateField("dt_end", event.target.value)}
            required
            isInvalid={!areDatesValid}
          />
        </Col>

        <Col md={4}>
          <Form.Label htmlFor="stage-language">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.LANGUAGE")} />
          </Form.Label>

          <Form.Select
            id="stage-language"
            value={form.language}
            onChange={(event) => _updateField("language", event.target.value)}
            required
          >
            {Object.values(LanguageEnum).map((value) => (
              <option key={value} value={value}>
                {translate(
                  language,
                  `PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.${value === LanguageEnum.FR ? "LANGUAGE_FR" : "LANGUAGE_EN"}`
                )}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={4}>
          <Form.Label htmlFor="stage-cadre">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.DIPLOMA_TYPE")} />
          </Form.Label>

          <Form.Select
            id="stage-cadre"
            value={form.default_cadre}
            onChange={(event) => _updateField("default_cadre", event.target.value)}
            required
          >
            {Object.values(CursusStageCadre).map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={4}>
          <Form.Label htmlFor="create-stage-priority">
            {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.PRIORITY")}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="create-cursus-stage-priority-tooltip">
                  {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.PRIORITY_HELP")}
                </Tooltip>
              }
            >
              <span className="dst-icon-info-circle ms-1 text-muted clickable" tabIndex={0} />
            </OverlayTrigger>
          </Form.Label>

          <Form.Control
            type="number"
            min="0"
            id="create-stage-priority"
            value={form.priority ?? ""}
            onChange={(event) =>
              _updateField("priority", event.target.value === "" ? null : Number(event.target.value))
            }
          />
        </Col>

        <Col md={12}>
          <div className="form-label">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.FLAGS")}</div>

          <MultiSelectDropdown
            language={language}
            label={
              selectedFlags.length
                ? selectedFlags.map((flag) => flag.name).join(", ")
                : translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.SELECT_FLAGS")
            }
            items={flags}
            mock={isFlagsLoading}
            disabled={isSaving}
            defaultSelectedItems={selectedFlags}
            onConfirm={(selected: Flag[]) => setSelectedFlags(selected)}
            displayItem={(flag) => flag.name}
            badgeItemProperty="name"
            toggleClass="w-100 border"
            showAlwaysConfirm
          />
        </Col>

        <Col md={8} className="d-flex align-items-end pb-2">
          <Form.Check className="d-flex align-items-center gap-2 ps-0" type="switch">
            <Form.Check.Input
              className="m-0 flex-shrink-0"
              id="cursus-stage-disabled"
              checked={form.is_disabled}
              onChange={(event) => _updateField("is_disabled", event.target.checked)}
            />

            <Form.Check.Label htmlFor="cursus-stage-disabled">
              {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.IS_DISABLED")}
            </Form.Check.Label>
          </Form.Check>
        </Col>
      </Row>
    </div>
  );

  const formatTab = (
    <div className="bg-body p-4 h-100 s-regular">
      <h3 className="mb-4">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.FORMAT")}</h3>

      <Row className="g-3">
        <Col md={12}>
          <Form.Label htmlFor="stage-format">
            <RequiredLabel label={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.FORMAT")} />
          </Form.Label>

          <Form.Select
            id="stage-format"
            value={form.format}
            onChange={(event) => _updateField("format", event.target.value)}
            required
          >
            {Object.values(CohortIntensity).map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Form.Select>
        </Col>

        {(
          ["total_hours", "hours_spent_on_project", "hours_spent_on_modules", "hours_spent_on_masterclass"] as const
        ).map((key) => (
          <Col md={6} key={key}>
            <Form.Label htmlFor={`create-stage-${key}`}>
              {translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.${key.toUpperCase()}`)}
            </Form.Label>

            <Form.Control
              type="number"
              min="0"
              id={`create-stage-${key}`}
              value={form[key] ?? ""}
              onChange={(event) => _updateField(key, event.target.value === "" ? null : Number(event.target.value))}
            />
          </Col>
        ))}
      </Row>
    </div>
  );

  const contractualizationTab = (
    <div className="bg-body p-4 h-100 s-regular">
      <h3 className="mb-4">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.CONTRACTUALIZATION")}</h3>

      <Row className="g-3">
        <Col md={12}>
          <div className="form-label">
            {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.PEDAGOGY_LEADER")}
          </div>

          <MultiSelectLiveDropdown
            category="users"
            isSingleSelect
            language={language}
            disabled={isSaving}
            label={
              selectedLeaders.length
                ? selectedLeaders[0].value.full_name ||
                  `${selectedLeaders[0].value.first_name} ${selectedLeaders[0].value.last_name}`
                : translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.SELECT_PEDAGOGY_LEADER")
            }
            onConfirm={(selected) => {
              setSelectedLeaders(selected);

              _updateField("pedagogy_leader_id", selected[0]?.value.id ?? null);
            }}
            toggleClass="w-100 border"
          />
        </Col>

        {textArea("objectives")}
        {textArea("prerequisites")}
        {textArea("content")}
        {textArea("training_organization")}
        {textArea("evaluations")}
        {textArea("end_of_formation_document")}
        {textArea("mediator")}
        {textArea("pedagogy_leader_references")}
        {textArea("pedagogy_leader_diplomas")}

        <Col md={12}>
          <Form.Label htmlFor="create-stage-presentation-url">
            {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.FIELDS.PRESENTATION_DOCUMENT_URL")}
          </Form.Label>

          <Form.Control
            id="create-stage-presentation-url"
            type="url"
            value={form.presentation_document_url}
            onChange={(event) => _updateField("presentation_document_url", event.target.value)}
          />
        </Col>
      </Row>
    </div>
  );

  const certifierTab = compliance ? (
    <div className="bg-body p-4 s-regular">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.TITLE")}</h3>

        <DstButton
          type="button"
          variant="outline-danger"
          btnImageBefore={<span className="dst-icon-trash-03 me-2" />}
          value={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.REMOVE")}
          clickFunction={() => setCompliance(null)}
        />
      </div>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="compliance-type">
            <Form.Label>{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.TYPE")}</Form.Label>

            <Form.Select
              value={compliance.type}
              onChange={(event) => {
                _updateComplianceField("type", event.target.value);
              }}
            >
              {COMPLIANCE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {(["name", "code", "certifier_name"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                <RequiredLabel
                  label={translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.${key.toUpperCase()}`)}
                />
              </Form.Label>

              <Form.Control
                value={compliance[key]}
                maxLength={255}
                required
                onChange={(event) => _updateComplianceField(key, event.target.value)}
              />
            </Form.Group>
          </Col>
        ))}

        {(["internship_needed", "end_of_study_defense"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Check className="d-flex align-items-center gap-2 ps-0" type="switch">
              <Form.Check.Input
                className="m-0 flex-shrink-0"
                id={`compliance-${key}`}
                checked={compliance[key]}
                onChange={(event) => _updateComplianceField(key, event.target.checked)}
              />

              <Form.Check.Label htmlFor={`compliance-${key}`}>
                {translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Check.Label>
            </Form.Check>
          </Col>
        ))}

        {(["internship_duration_hours", "internship_duration_days", "end_of_study_duration"] as const).map((key) => (
          <Col md={6} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Label>

              <Form.Control
                type="number"
                min={0}
                step={1}
                value={compliance[key] ?? ""}
                onChange={(event) =>
                  _updateComplianceField(key, event.target.value === "" ? null : Number(event.target.value))
                }
              />
            </Form.Group>
          </Col>
        ))}

        <Col md={6}>
          <Form.Group controlId="compliance-supervision">
            <Form.Label>
              {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.END_OF_STUDY_EXTERNAL_SUPERVIEW")}
            </Form.Label>

            <Form.Select
              value={
                compliance.end_of_study_external_superview === null
                  ? ""
                  : String(compliance.end_of_study_external_superview)
              }
              onChange={(event) =>
                _updateComplianceField(
                  "end_of_study_external_superview",
                  event.target.value === "" ? null : event.target.value === "true"
                )
              }
            >
              <option value="">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.UNSPECIFIED")}</option>

              <option value="true">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.YES")}</option>

              <option value="false">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.NO")}</option>
            </Form.Select>
          </Form.Group>
        </Col>

        {(["internship_exemption_conditions", "end_of_study_conditions"] as const).map((key) => (
          <Col md={12} key={key}>
            <Form.Group controlId={`compliance-${key}`}>
              <Form.Label>
                {translate(language, `PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.${key.toUpperCase()}`)}
              </Form.Label>

              <Form.Control
                as="textarea"
                rows={3}
                value={compliance[key] ?? ""}
                onChange={(event) => _updateComplianceField(key, event.target.value || null)}
              />
            </Form.Group>
          </Col>
        ))}

        <Col md={12}>
          <Form.Group controlId="compliance-links">
            <Form.Label>{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.USEFUL_LINKS")}</Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              value={(() => {
                try {
                  const links: unknown = JSON.parse(compliance.useful_links);

                  return Array.isArray(links) ? links.join("\n") : compliance.useful_links;
                } catch {
                  return compliance.useful_links;
                }
              })()}
              onChange={(event) =>
                _updateComplianceField("useful_links", JSON.stringify(event.target.value.split("\n")))
              }
            />

            <Form.Text>{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.LINKS_HELP")}</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h4 className="mt-4 mb-3">{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.SKILL_BLOCKS")}</h4>

      {compliance.skill_blocks.length === 0 && (
        <div className="border rounded p-3 text-center text-muted">
          {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.NO_BLOCKS")}
        </div>
      )}

      <DragDropContext onDragEnd={_handleSkillBlockDragEnd}>
        <Droppable droppableId="skill-blocks">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {compliance.skill_blocks.map((block, index) => (
                <Draggable key={`new-skill-block-${index}`} draggableId={`new-skill-block-${index}`} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="border rounded bg-body p-2 mb-2"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          {...provided.dragHandleProps}
                          className="dst-icon-align-justify d-flex align-items-center align-self-stretch bg-primary-subtle text-primary border rounded px-2"
                        />

                        <Form.Control
                          size="sm"
                          aria-label={`${translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.NAME")} ${
                            index + 1
                          }`}
                          value={block.name}
                          required
                          maxLength={255}
                          placeholder={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.NAME")}
                          onChange={(event) => _updateSkillBlock(index, "name", event.target.value)}
                        />

                        <div className="d-flex align-items-center justify-content-center text-nowrap bg-body-tertiary border rounded px-2 py-1 ms-2">
                          <Form.Check className="d-flex align-items-center gap-2 ps-0 mb-0">
                            <Form.Check.Input
                              className="m-0"
                              id={`block-${index}-mandatory`}
                              checked={block.is_mandatory}
                              onChange={(event) => _updateSkillBlock(index, "is_mandatory", event.target.checked)}
                            />

                            <Form.Check.Label className="mb-0" htmlFor={`block-${index}-mandatory`}>
                              {translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.IS_MANDATORY")}
                            </Form.Check.Label>
                          </Form.Check>
                        </div>

                        <div className="border-start ms-2 ps-3">
                          <DstButton
                            type="button"
                            variant="danger"
                            btnClass="px-2 py-1"
                            btnImageBefore={<span className="dst-icon-trash-03" />}
                            value=""
                            tooltip={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.REMOVE")}
                            clickFunction={() =>
                              _updateComplianceField(
                                "skill_blocks",
                                compliance.skill_blocks
                                  .filter((_, position) => position !== index)
                                  .map((item, order) => ({
                                    ...item,
                                    order,
                                  }))
                              )
                            }
                          />
                        </div>
                      </div>

                      <Form.Control
                        className="mt-2"
                        size="sm"
                        as="textarea"
                        rows={2}
                        aria-label={`${translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.CONTENT")} ${
                          index + 1
                        }`}
                        value={block.content}
                        placeholder={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.CONTENT")}
                        onChange={(event) => _updateSkillBlock(index, "content", event.target.value)}
                      />
                    </div>
                  )}
                </Draggable>
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="mt-3">
        <DstButton
          type="button"
          variant="outline-primary"
          btnSize="sm"
          btnWidth="auto"
          btnImageBefore={<span className="dst-icon-plus me-2" />}
          value={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.ADD")}
          clickFunction={() =>
            _updateComplianceField("skill_blocks", [
              ...compliance.skill_blocks,
              {
                name: "",
                content: "",
                is_mandatory: false,
                order: compliance.skill_blocks.length,
              },
            ])
          }
        />
      </div>
    </div>
  ) : (
    <div className="bg-body p-4">
      <p>{translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CERTIFIER.EMPTY")}</p>

      <DstButton
        type="button"
        variant="primary"
        value={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.ADD_CERTIFICATION")}
        clickFunction={() => setCompliance(emptyComplianceForm())}
      />
    </div>
  );

  return (
    <Popup
      show={openPopup}
      onClose={_handleClose}
      modalTitle={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TITLE")}
      size="xl"
      scrollable
      removePadding
      footerContent={
        <React.Fragment>
          <DstButton
            variant="light"
            value={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.CANCEL")}
            clickFunction={_handleClose}
            disabled={isSaving}
          />

          <DstButton
            type="submit"
            variant="primary"
            value={translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.SAVE")}
            clickFunction={_handleSubmit}
            disabled={isSaving || !_isFormValid}
            loading={isSaving}
          />
        </React.Fragment>
      }
    >
      <Form id="create-cursus-stage-form" onSubmit={_handleSubmit}>
        <fieldset disabled={isSaving}>
          <DstTabsVertical
            activeKey={activeTab}
            bodyClass="medium-gray-background p-3"
            classes="pt-0 create-cursus-stage-tabs"
            onSelect={(key) => key && setActiveTab(key)}
            titleIcon={<React.Fragment />}
            variant="light"
            TabsSize={{
              xs: 12,
              sm: 4,
              md: 3,
              lg: 3,
              xl: 3,
            }}
            tabsData={[
              {
                id: "general",
                title: translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.OVERALL_INFORMATION"),
                content: overallInformationTab,
              },
              {
                id: "format",
                title: translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.FORMAT"),
                content: formatTab,
              },
              {
                id: "contractualization",
                title: translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.CONTRACTUALIZATION"),
                content: contractualizationTab,
              },
              {
                id: "certifier",
                title: translate(language, "PAGES.COURSE.MODALS.CREATE_STAGE.TABS.CERTIFIER"),
                content: certifierTab,
              },
            ]}
          />
        </fieldset>
      </Form>
    </Popup>
  );
};

export default CreateCursusStagePopup;
